import { DatePicker, Form, Select } from 'antd';
import dayjs from 'dayjs';
import { cn } from '@/libs/utils';
import { DATE_FORMATS } from '@/utils/formUtils';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';
import { EmploymentTypeControl } from '../EmploymentTypeControl';

/**
 * The "Contract" tile of the employee profile: one card with the employment
 * type on top and status / dates in a row under it. Same fields, rules and
 * save as EmployeePersonalFields (status) and EmployeeContractFields, whose
 * contract fields step aside on the profile.
 */
export default function EmployeeContractProfilePanel() {
  const {
    effectiveDateItem,
    employmentTypeKeys,
    employmentTypeState,
    endDateKeys,
    endDateState,
    inSection,
    sectionClass,
    showEndDate,
    startDate,
    startDateKeys,
    startDateState,
    statusKeys,
    statusOptions,
    statusState,
    t,
  } = useEmployeeGeneralDataForm();
  const f = (key) => t(key, { ns: 'forms' });
  const required = [
    { required: true, message: f('formValidation.selectDateRequired') },
  ];

  return (
    <section className={sectionClass('ugovor')}>
      <h3 className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
        {t('employeeProfile.contractUi.title', { ns: 'translations' })}
      </h3>
      <div className="setting-card-block rounded-xl border border-zinc-200 bg-white px-3 pt-2.5">
        {employmentTypeState?.visible && (
          <Form.Item
            name={employmentTypeKeys}
            label={f('employmentType')}
            // The three categories side by side, not one per row.
            className="[&_.ant-radio-group]:!flex-nowrap [&_.ant-radio-group]:gap-2 [&_.ant-radio-wrapper]:!w-auto [&_.ant-radio-wrapper]:flex-1"
          >
            <EmploymentTypeControl
              disabled={employmentTypeState?.disabled}
              t={t}
            />
          </Form.Item>
        )}

        {/* Status and dates share one row, each the same width. */}
        <div className={cn('flex gap-4 [&>*]:min-w-0 [&>*]:flex-1')}>
          {statusState?.visible && (
            <Form.Item
              label={t('status', { ns: 'tables' })}
              name={statusKeys}
              rules={[
                {
                  required: true,
                  message: f('formValidation.statusRequired'),
                },
              ]}
            >
              <Select
                allowClear
                mode="single"
                placeholder={t('status', { ns: 'tables' })}
                className="w-full"
                options={statusOptions}
                disabled={statusState?.disabled}
              />
            </Form.Item>
          )}

          {startDateState?.visible && (
            <Form.Item
              name={startDateKeys}
              label={f('startDate')}
              rules={required}
            >
              <DatePicker
                className="w-full"
                format={DATE_FORMATS.DISPLAY}
                placeholder={f('startDateOfWork')}
                disabled={startDateState?.disabled}
              />
            </Form.Item>
          )}

          {showEndDate && endDateState?.visible && (
            <Form.Item name={endDateKeys} label={f('endDate')} rules={required}>
              <DatePicker
                minDate={dayjs(startDate, DATE_FORMATS.STANDARD)}
                className="w-full"
                format={DATE_FORMATS.DISPLAY}
                placeholder={f('endDateOfWork')}
                disabled={endDateState?.disabled}
              />
            </Form.Item>
          )}

          {/* The Working time tile shows it too (EmployeeContractFields). */}
          {inSection('ugovor') && effectiveDateItem}
        </div>
      </div>
    </section>
  );
}
